"use client";

import type { ComponentProps, ElementType, ReactNode } from "react";
import { Alert, AlertDescription, Button } from "@zapier/design-system";
import { useCreateTemplate } from "src/components/TemplateLibraryDetail/useCreateTemplate";
import { TemplateCreationModal } from "src/components/TemplateCreationModal";
import { LibraryTemplate } from "src/server/zsl/templates/types";
import styles from "./UseTemplateButton.module.css";
import classNames from "classnames";
import { createPortal } from "react-dom";

import type { Product } from "src/utils/products";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";

type Props = {
  children: ReactNode;
  component?: ElementType;
  products: Product[];
  slug: LibraryTemplate["meta"]["slug"];
  isZapTemplate?: boolean;
} & ComponentProps<"button">;

export const UseTemplateButton = ({
  children,
  component: Component = Button,
  slug,
  products,
  isZapTemplate = false,
  ...rest
}: Props) => {
  const { createTemplate, creationScreen, error, setError } = useCreateTemplate(
    false,
    isZapTemplate,
  );

  const handleClick = () => {
    emitUserInteractionEvent(UserInteractionEventName.templateCardClicked, {
      event_action: "click",
      option_selected: slug,
    });
    createTemplate(slug);
  };

  return (
    <>
      {error &&
        createPortal(
          <div className={classNames(styles.alertContainer)}>
            <Alert
              onDismiss={() => {
                setError(null);
              }}
              type="error"
              layout="default"
              title="Error"
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>,
          document.body,
        )}
      {creationScreen && (
        <TemplateCreationModal slug={slug} products={products} />
      )}
      <Component {...rest} onClick={handleClick}>
        {children}
      </Component>
    </>
  );
};
